/* eslint-disable max-len */
/* eslint-disable react/no-array-index-key */
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  AppRegistration as AppRegistrationIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getAllTenants, getAllPlans } from '../../utils/api';

// Définition de données statiques par défaut si les API échouent
const DEFAULT_STATS = {
  totalTenants: 0,
  activeTenants: 0,
  totalPlans: 0,
};

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataLoadFailed, setDataLoadFailed] = useState(false);
  const [stats, setStats] = useState(DEFAULT_STATS);

  const fetchDashboardData = async () => {
    console.log('Dashboard: Début du chargement des données');
    setLoading(true);
    setError(null);

    try {
      // Vérifier que le token est présent
      const token = localStorage.getItem('token');
      console.log('Dashboard: Token présent?', !!token);

      if (!token) {
        setError('Token d\'authentification manquant');
        setDataLoadFailed(true);
        setLoading(false);
        return;
      }

      // Récupérer toutes les données nécessaires
      let tenantsData = [];
      let plansData = [];

      try {
        console.log('Dashboard: Appel API tenants');
        tenantsData = await getAllTenants();
        console.log('Dashboard: Données des tenants récupérées', tenantsData);
      }
      catch (tenantError) {
        console.error('Dashboard: Erreur lors de la récupération des tenants', tenantError);
        setError(`Erreur tenants: ${tenantError.message}`);
        setDataLoadFailed(true);
      }

      try {
        console.log('Dashboard: Appel API plans');
        plansData = await getAllPlans();
        console.log('Dashboard: Données des plans récupérées', plansData);
      }
      catch (planError) {
        console.error('Dashboard: Erreur lors de la récupération des plans', planError);
        if (!error) {
          setError(`Erreur plans: ${planError.message}`);
        }
        setDataLoadFailed(true);
      }

      // Calculer les statistiques avec les données disponibles
      const activeTenants = Array.isArray(tenantsData)
        ? tenantsData.filter((tenant) => tenant.status === 'active').length
        : 0;

      setStats({
        totalTenants: Array.isArray(tenantsData) ? tenantsData.length : 0,
        activeTenants,
        totalPlans: Array.isArray(plansData) ? plansData.length : 0,
      });

      console.log('Dashboard: Statistiques mises à jour', {
        totalTenants: Array.isArray(tenantsData) ? tenantsData.length : 0,
        activeTenants,
        totalPlans: Array.isArray(plansData) ? plansData.length : 0,
      });
    }
    catch (err) {
      console.error('Dashboard: Erreur générale lors du chargement du dashboard:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données');
      setDataLoadFailed(true);
    }
    finally {
      setLoading(false);
      console.log('Dashboard: Fin du chargement des données');
    }
  };

  useEffect(() => {
    // Ajouter un délai pour laisser le temps à l'UI de se rendre correctement
    const timer = setTimeout(() => {
      fetchDashboardData();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  console.log('Dashboard: État actuel:', {
    loading, error, stats, dataLoadFailed
  });

  const handleRetry = () => {
    console.log('Dashboard: Tentative de rechargement des données');
    fetchDashboardData();
  };

  // Statut de chargement
  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        flexDirection: 'column'
      }}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Chargement du tableau de bord...
        </Typography>
      </Box>
    );
  }

  // Cartes statistiques
  const statCards = [
    {
      title: 'Tenants Totaux',
      value: dataLoadFailed ? '0' : stats.totalTenants,
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      color: '#e3f2fd',
    },
    {
      title: 'Tenants Actifs',
      value: dataLoadFailed ? '0' : stats.activeTenants,
      icon: <StorageIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      color: '#e8f5e9',
    },
    {
      title: 'Plans Disponibles',
      value: dataLoadFailed ? '0' : stats.totalPlans,
      icon: <AttachMoneyIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      color: '#fff8e1',
    },
    {
      title: 'Fonctionnalités',
      value: '10+',
      icon: <AppRegistrationIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      color: '#e0f7fa',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Tableau de bord
      </Typography>

      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 4 }}
          action={(
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
            >
              Réessayer
            </Button>
          )}
        >
          <AlertTitle>Problème de chargement des données</AlertTitle>
          {error}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Le tableau de bord est affiché avec des informations limitées.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ bgcolor: card.color, boxShadow: 2, height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  {card.icon}
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                  {card.value}
                </Typography>
                <Typography color="text.secondary">
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Bienvenue dans l'interface d'administration de Drivesoft
        </Typography>
        <Typography variant="body1">
          Depuis ce tableau de bord, vous pouvez gérer tous les aspects de votre plateforme SaaS multi-tenant :
        </Typography>
        <Box component="ul" sx={{ mt: 1 }}>
          <Typography component="li">Créer et gérer des tenants</Typography>
          <Typography component="li">Configurer les plans d'abonnement</Typography>
          <Typography component="li">Activer ou désactiver des fonctionnalités par tenant</Typography>
          <Typography component="li">Suivre l'utilisation et les statistiques</Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default Dashboard;
